import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WelcomeOffer from "@/components/WelcomeOffer";
import ExitIntentPopup from "@/components/ExitIntentPopup";
import SocialProofNotifications from "@/components/SocialProofNotifications";
import UrgencyBar from "@/components/UrgencyBar";
import FloatingChatButton from "@/components/FloatingChatButton";
import Home from "@/pages/Home";
import Services from "@/pages/Services";
import Pricing from "@/pages/Pricing";
import Booking from "@/pages/Booking";
import BookingSuccess from "@/pages/BookingSuccess";
import Gallery from "@/pages/Gallery";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
// TEMPORARILY REMOVED - Re-add later:
// import PurchaseBeats from "@/pages/PurchaseBeats";
// import BeatPurchaseSuccess from "@/pages/BeatPurchaseSuccess";
// import MusicLessons from "@/pages/MusicLessons";
import DJLessons from "@/pages/DJLessons";
import Photography from "@/pages/Photography";
import JuniorDJAcademy from "@/pages/JuniorDJAcademy";
import JuniorProgress from "@/pages/JuniorProgress";
import GiftVouchers from "@/pages/GiftVouchers";
import DjMembership from "@/pages/DjMembership";
import MembershipSuccess from "@/pages/MembershipSuccess";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";

// Page titles for each route
const pageTitles = {
  "/": "Home | Dream Makers Recording Group",
  "/services": "Services | Dream Makers Recording Group",
  "/pricing": "Pricing | Dream Makers Recording Group",
  "/booking": "Book Now | Dream Makers Recording Group",
  "/booking/success": "Booking Confirmed | Dream Makers Recording Group",
  "/gallery": "Gallery | Dream Makers Recording Group",
  // TEMPORARILY REMOVED:
  // "/beats": "Purchase Beats | Dream Makers Recording Group",
  // "/beats/success": "Purchase Complete | Dream Makers Recording Group",
  // "/music-lessons": "Guitar, Bass & Drum Lessons | Dream Makers Recording Group",
  "/lessons": "DJ & Production Lessons | Dream Makers Recording Group",
  "/photography": "Photography | Dream Makers Recording Group",
  "/junior-dj-academy": "Junior DJ Academy (Ages 10–17) | Dream Makers Recording Group",
  "/about": "About Us | Dream Makers Recording Group",
  "/contact": "Contact | Dream Makers Recording Group",
  "/dj-memberships": "DMRG Sessions Memberships | Dream Makers Recording Group",
  "/membership-success": "Welcome to Dream Makers | Dream Makers Recording Group",
  "/admin": "Admin Login | Dream Makers",
  "/admin/login": "Admin Login | Dream Makers",
  "/admin/dashboard": "Admin Dashboard | Dream Makers",
};

// Global auth handler - catches session_id on ANY page (Safari fix)
function GlobalAuthHandler() {
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    // Check for session_id in hash on any page
    const hash = window.location.hash;
    if (hash?.includes('session_id=')) {
      // Redirect to admin dashboard which will handle the auth
      const fullUrl = '/admin/dashboard' + hash;
      navigate(fullUrl, { replace: true });
    }
  }, [navigate, location]);
  
  return null;
}

// Scroll to top and update page title on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Update page title
    const title = pageTitles[pathname] || "Dream Makers Recording Group";
    document.title = title;
  }, [pathname]);
  
  return null;
}

// Layout component that conditionally shows navbar/footer
function Layout({ children }) {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  
  return (
    <>
      {!isAdminRoute && <Navbar />}
      <main>{children}</main>
      {!isAdminRoute && <Footer />}
    </>
  );
}

// Router component
function AppRouter() {
  // NOTE: session_id hash from Emergent OAuth is handled inside AdminDashboard's initAuth.
  // We previously short-circuited to <AuthCallback /> here, but that caused redirect loops
  // when navigate() preserved the hash (Chrome would throttle further navigation).
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/services" element={<Services />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/booking" element={<Booking />} />
        <Route path="/booking/success" element={<BookingSuccess />} />
        <Route path="/gallery" element={<Gallery />} />
        {/* TEMPORARILY REMOVED - Re-add later:
        <Route path="/beats" element={<PurchaseBeats />} />
        <Route path="/beats/success" element={<BeatPurchaseSuccess />} />
        <Route path="/music-lessons" element={<MusicLessons />} />
        */}
        <Route path="/lessons" element={<DJLessons />} />
        <Route path="/photography" element={<Photography />} />
        <Route path="/junior-dj-academy" element={<JuniorDJAcademy />} />
        <Route path="/junior-progress/:token" element={<JuniorProgress />} />
        <Route path="/gift-vouchers" element={<GiftVouchers />} />
        <Route path="/dj-memberships" element={<DjMembership />} />
        <Route path="/membership-success" element={<MembershipSuccess />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/admin" element={<AdminLogin />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/dashboard/*" element={<AdminDashboard />} />
      </Routes>
    </Layout>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-dream-bg">
      <BrowserRouter>
        <GlobalAuthHandler />
        <ScrollToTop />
        <PublicChrome />
        <AppRouter />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </div>
  );
}

// Renders public-facing widgets (urgency bar, popups, chat) only on non-admin routes.
function PublicChrome() {
  const { pathname } = useLocation();
  if (pathname.startsWith('/admin') || pathname.startsWith('/junior-progress')) return null;
  return (
    <>
      <UrgencyBar />
      <WelcomeOffer />
      <ExitIntentPopup />
      <SocialProofNotifications />
      <FloatingChatButton />
    </>
  );
}

export default App;
